/**
 * Keyboard shortcuts for the Preview toolbar (Issue #3313).
 *
 * Every key here comes from the `shortcut` field of a command in `preview-commands.ts`, which the
 * menus also show, so a key and its menu label cannot disagree. A key sends the same command as
 * the menu item, through the Preview's toolbar command handler.
 *
 * Two keys in the menus are not dispatched from here: Escape, which the active tool handles one
 * level at a time before it exits, and Ctrl/⌘+F, which opens Find.
 */

import { PREVIEW_AREA_SHAPES, PREVIEW_COMMANDS, PreviewAreaShape, PreviewCommandId } from './preview-commands';

/** What a shortcut does: a toolbar command, with the Select area shape for `selectAreaShape`. */
export interface PreviewShortcutTarget {
  command: PreviewCommandId;
  areaShape?: PreviewAreaShape;
}

/** One row of the shortcut list. */
export interface PreviewShortcut {
  /** What the key does, as the menu names it. */
  label: string;
  /** The key as the menu shows it, for example `Shift+R`. */
  shortcut: string;
  /** The command it sends, or null for a key handled elsewhere (Escape, Find, the list itself). */
  target: PreviewShortcutTarget | null;
}

/** The key that opens the shortcut list. */
export const SHORTCUT_LIST_KEY = '?';

/** Keys shown in the menus that another part of the Preview handles. */
const HANDLED_ELSEWHERE = new Set(['Esc']);

/** Every shortcut, in menu order, followed by Find and the shortcut list itself. */
export const PREVIEW_SHORTCUTS: readonly PreviewShortcut[] = [
  ...PREVIEW_COMMANDS.filter(command => command.shortcut).map(command => ({
    label: command.label,
    shortcut: command.shortcut!,
    target: HANDLED_ELSEWHERE.has(command.shortcut!) ? null : { command: command.id },
  })),
  ...PREVIEW_AREA_SHAPES.filter(option => option.shortcut).map(option => ({
    label: option.label,
    shortcut: option.shortcut!,
    target: { command: 'selectAreaShape' as const, areaShape: option.shape },
  })),
  { label: 'Find text', shortcut: 'Ctrl/⌘+F', target: null },
  { label: 'Keyboard shortcuts', shortcut: SHORTCUT_LIST_KEY, target: null },
];

/**
 * The keys a shortcut accepts. A letter is matched by its letter and by whether Shift is held, so
 * `R` and `Shift+R` are different keys and Caps Lock does not change either. `+` and `−` also take
 * the unshifted `=` and `-` keys they share a key cap with, and `−` the Unicode minus sign that some
 * layouts send.
 */
function keysFor(shortcut: string): { keys: string[]; shift: boolean | null } {
  const shifted = shortcut.startsWith('Shift+');
  const key = shifted ? shortcut.slice('Shift+'.length) : shortcut;
  if (key === '+') return { keys: ['+', '='], shift: null };
  if (key === '−' || key === '-') return { keys: ['-', '_', '−'], shift: null };
  if (/^[A-Z]$/.test(key)) return { keys: [key.toLowerCase()], shift: shifted };
  return { keys: [key], shift: shifted ? true : null };
}

const KEY_MAP: readonly { keys: string[]; shift: boolean | null; target: PreviewShortcutTarget }[] =
  PREVIEW_SHORTCUTS.filter(entry => entry.target).map(entry => ({ ...keysFor(entry.shortcut), target: entry.target! }));

/**
 * The command a key press sends, or null when it is not a Preview shortcut. Ctrl, ⌘ and Alt belong
 * to the browser and the operating system, so a key held with any of them is never a shortcut.
 */
export function matchPreviewShortcut(event: KeyboardEvent): PreviewShortcutTarget | null {
  if (event.ctrlKey || event.metaKey || event.altKey || !event.key) return null;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  const entry = KEY_MAP.find(
    candidate => candidate.keys.includes(key) && (candidate.shift === null || candidate.shift === event.shiftKey)
  );
  return entry ? entry.target : null;
}

/** True for the key that opens the shortcut list. */
export function isShortcutListKey(event: KeyboardEvent): boolean {
  return !event.ctrlKey && !event.metaKey && !event.altKey && event.key === SHORTCUT_LIST_KEY;
}

/**
 * True when a key press is meant for something being typed into or a dialog, so no single-key
 * shortcut may act on it: a text field, a select, an editable element, a dialog, or an open menu
 * or list box (which use letters to jump between their items).
 *
 * The Preview's Zoom panel is a small dialog too. Only `+` and `−` keep working there, as they did
 * before shortcuts were added, because the panel's own buttons for them are labelled with the keys.
 */
export function isShortcutBlockedTarget(
  eventTarget: EventTarget | null,
  shortcut: PreviewShortcutTarget | null
): boolean {
  const element = eventTarget instanceof Element ? eventTarget : null;
  if (!element) return false;
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    (element instanceof HTMLElement && element.isContentEditable) ||
    // The attribute check also covers test DOMs that do not compute isContentEditable.
    element.closest('[contenteditable]:not([contenteditable="false"])')
  ) {
    return true;
  }
  if (element.closest('[role="menu"], [role="listbox"]')) return true;
  const dialog = element.closest('[role="dialog"], [role="alertdialog"], [aria-modal="true"], mat-dialog-container');
  if (!dialog) return false;
  const zoomKey = shortcut?.command === 'zoomIn' || shortcut?.command === 'zoomOut';
  return !(zoomKey && dialog.classList.contains('preview-toolbar-menu'));
}
